import { useMemo, useState } from "react";
import SectionHeading from "./SectionHeading";

const CATS = ["全部", "娱乐", "社会", "科技", "影视", "美食", "校园"] as const;
type Cat = (typeof CATS)[number];

type Topic = {
  id: number;
  emoji: string;
  category: string;
  title: string;
  desc: string;
  comments: number;
  bullets: number;
  viewers: string;
  hot: boolean;
  tile: string;
};

const TOPICS: Topic[] = [
  { id: 1, emoji: "🎬", category: "影视", title: "本季最佳片单空降，你被哪部圈粉了？", desc: "网友票选年度黑马剧，评论区吵翻了但评分很诚实。", comments: 12846, bullets: 3920, viewers: "18.2万", hot: true, tile: "from-primary-100 to-primary-50" },
  { id: 2, emoji: "👀", category: "社会", title: "低头族的自救指南：你的眼睛还好吗？", desc: "打工人与手机的一天，网友纷纷晒出护眼小妙招。", comments: 9842, bullets: 2110, viewers: "12.6万", hot: true, tile: "from-accent-100 to-accent-50" },
  { id: 3, emoji: "🤖", category: "科技", title: "AI 生成的神回复到底有多离谱？", desc: "从段子到情书，网友实测各种生成式 AI，笑不活了。", comments: 15320, bullets: 5070, viewers: "23.9万", hot: true, tile: "from-secondary-100 to-secondary-50" },
  { id: 4, emoji: "🍜", category: "美食", title: "泡面的一百种神仙吃法，深夜党狂喜", desc: "加蛋是底线，加什么都行，评论区人均大厨。", comments: 7305, bullets: 1660, viewers: "9.7万", hot: false, tile: "from-primary-100 to-primary-50" },
  { id: 5, emoji: "🏫", category: "校园", title: "开学季生存法则，老学长学姐的血泪经验", desc: "那些年踩过的坑，全在评论区里长教训了。", comments: 6512, bullets: 1402, viewers: "7.4万", hot: false, tile: "from-accent-100 to-accent-50" },
  { id: 6, emoji: "🎤", category: "娱乐", title: "出道即巅峰的舞台名场面盘点", desc: "高能预警，每一帧都值得反复回看，弹幕都刷疯了。", comments: 20143, bullets: 8230, viewers: "31.5万", hot: true, tile: "from-secondary-100 to-secondary-50" },
  { id: 7, emoji: "😋", category: "美食", title: "本地人私藏的宝藏小店清单", desc: "只有本地人才知道的巷子美食，评论区直接变地图。", comments: 8840, bullets: 1730, viewers: "10.9万", hot: false, tile: "from-primary-100 to-primary-50" },
  { id: 8, emoji: "💻", category: "科技", title: "无纸化学习一年后，我后悔了吗？", desc: "平板电脑和手写笔记的大混战，谁用谁知道。", comments: 5906, bullets: 1105, viewers: "6.8万", hot: false, tile: "from-accent-100 to-accent-50" },
  { id: 9, emoji: "🚌", category: "社会", title: "通勤路上的那些名场面，打工人共情了", desc: "早高峰的公交地铁，每天都上演真实版生存大作战。", comments: 11072, bullets: 2890, viewers: "15.1万", hot: true, tile: "from-secondary-100 to-secondary-50" },
  { id: 10, emoji: "🎓", category: "校园", title: "毕业合影整活大赛，谁的创意最顶？", desc: "从学士服到集体摆拍，青春就该这么放肆。", comments: 4913, bullets: 980, viewers: "5.6万", hot: false, tile: "from-primary-100 to-primary-50" },
  { id: 11, emoji: "🎮", category: "娱乐", title: "最会上分的电竞时刻，满屏六六六", desc: "操作拉满的瞬间，弹幕和评论直接双厨狂喜。", comments: 13477, bullets: 6405, viewers: "20.3万", hot: true, tile: "from-accent-100 to-accent-50" },
  { id: 12, emoji: "🌿", category: "社会", title: "阳台种菜人的快乐，你真的想象不到", desc: "从西红柿到小葱，当代年轻人的精神自留地。", comments: 4201, bullets: 812, viewers: "4.9万", hot: false, tile: "from-secondary-100 to-secondary-50" },
];

export default function TopicFeed() {
  const [active, setActive] = useState<Cat>("全部");
  const [liked, setLiked] = useState<Record<number, boolean>>({});

  const list = useMemo(
    () => (active === "全部" ? TOPICS : TOPICS.filter((t) => t.category === active)),
    [active]
  );

  return (
    <section id="topics" className="bg-background-50 py-16 md:py-20">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
        <SectionHeading
          eyebrow="话题广场"
          title="挑一个你感兴趣的话题"
          accent="进来聊聊"
          desc="按兴趣分类浏览全网热聊话题，点进评论区你就是最会接梗的瓜友。"
        />

        <div className="mb-8 flex flex-wrap items-center justify-center">
          <div className="flex flex-wrap items-center justify-center gap-1 rounded-full bg-background-100 p-1">
            {CATS.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActive(cat)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  active === cat
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:text-primary-700 hover:bg-background-50"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((topic) => (
            <article
              key={topic.id}
              className="pop-in group flex flex-col rounded-2xl bg-background-100/80 p-5 ring-1 ring-background-200/70 hover:ring-primary-200 transition"
            >
              <div className="flex items-center justify-between gap-3">
                <span
                  className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${topic.tile} text-2xl`}
                >
                  {topic.emoji}
                </span>
                <span className="flex items-center gap-1.5">
                  {topic.hot && (
                    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-100 px-2 py-1 text-xs text-primary-700">
                      <i className="ri-fire-line"></i>
                      热议
                    </span>
                  )}
                  <span className="whitespace-nowrap rounded-full bg-secondary-100 px-2 py-1 text-xs text-secondary-800">
                    {topic.category}
                  </span>
                </span>
              </div>

              <h3 className="mt-4 text-base font-semibold leading-snug text-foreground-900">
                {topic.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600 flex-1">{topic.desc}</p>

              <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-sm text-foreground-500">
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-chat-1-line text-primary-500"></i>
                  {topic.comments.toLocaleString()}
                </span>
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-bubble-chart-line text-accent-600"></i>
                  {topic.bullets.toLocaleString()}
                </span>
                <button
                  type="button"
                  aria-label={liked[topic.id] ? "已围观，点击取消" : "围观这个话题"}
                  onClick={() => setLiked((p) => ({ ...p, [topic.id]: !p[topic.id] }))}
                  className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 transition-colors ${
                    liked[topic.id]
                      ? "bg-primary-100 text-primary-700"
                      : "hover:bg-primary-50 text-foreground-600 hover:text-primary-600"
                  }`}
                >
                  <i className={liked[topic.id] ? "ri-eye-fill" : "ri-eye-line"}></i>
                  {liked[topic.id] ? "已围观" : "围观"}
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}